// frontend/src/pages/ApiKeysPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAuth } from "../context/AuthContext";
import { getUsage } from "../api/orgs";
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
} from "../api/apiKeys";
import CreateApiKeyModal from "../components/CreateApiKeyModal";
import ConfirmDialog from "../components/ConfirmDialog";
import Spinner from "../components/Spinner";

/** Format an ISO timestamp as a short local date, or a dash when null. */
function fmtDate(ts) {
  return ts ? new Date(ts).toLocaleDateString() : "—";
}

/** Small monospace pill for a scope. */
function ScopeBadge({ scope }) {
  return (
    <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
      {scope}
    </span>
  );
}
ScopeBadge.propTypes = { scope: PropTypes.string.isRequired };

/**
 * KeyRevealModal — shows a freshly created raw key exactly once.
 */
function KeyRevealModal({ rawKey, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-3">
          Your new API key
        </h2>

        <div className="rounded-lg bg-gray-900 px-4 py-3 mb-3">
          <code
            data-testid="raw-key"
            className="block break-all font-mono text-sm text-green-300"
          >
            {rawKey}
          </code>
        </div>

        <p className="mb-4 text-sm font-medium text-red-600">
          This key will never be shown again. Copy it now.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={copy}
            className="px-4 py-2 text-sm rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Copy
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
KeyRevealModal.propTypes = {
  rawKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * ApiKeysPage — manage the active org's API keys.
 *
 * Section 1: upgrade prompt when the plan has no API access (no other content).
 * Section 2: "Create New API Key" → CreateApiKeyModal → one-time key reveal.
 * Section 3: table of keys with a Revoke action (confirmation required).
 *
 * Creating / revoking require owner or admin role; plain members see the table
 * read-only (the backend enforces both the role and the plan gate).
 */
function ApiKeysPage() {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const role = currentOrg?.role;
  const canManage = role === "owner" || role === "admin";

  const [loading, setLoading] = useState(true);
  const [hasApiAccess, setHasApiAccess] = useState(false);
  const [keys, setKeys] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revealKey, setRevealKey] = useState(null); // raw key, shown once
  const [confirmRevoke, setConfirmRevoke] = useState(null); // {id, name}
  const [revoking, setRevoking] = useState(false);

  const loadKeys = useCallback(async () => {
    setKeys(await listApiKeys());
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const usage = await getUsage();
        const apiAccess = Boolean(usage?.limits?.api_access);
        if (!active) return;
        setHasApiAccess(apiAccess);
        if (apiAccess) await loadKeys();
      } catch {
        if (active) toast.error("Could not load API keys.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadKeys]);

  const handleCreate = async ({ name, scopes, expiresAt }) => {
    setCreating(true);
    try {
      const created = await createApiKey({ name, scopes, expiresAt });
      setShowCreate(false);
      setRevealKey(created.key);
      toast.success("API key created.");
      await loadKeys();
    } catch {
      toast.error("Could not create API key.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await revokeApiKey(confirmRevoke.id);
      toast.success("API key revoked.");
      setConfirmRevoke(null);
      await loadKeys();
    } catch {
      toast.error("Could not revoke API key.");
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  // ── Section 1: upgrade prompt (no other content when locked) ─────────────
  if (!hasApiAccess) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <svg
          className="mx-auto mb-4 h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <h1 className="mb-2 text-lg font-semibold text-gray-900">
          API access is a Pro feature
        </h1>
        <p className="mb-6 text-sm text-gray-600">
          Upgrade your plan to create API keys and access QRFlow
          programmatically.
        </p>
        <button
          onClick={() => navigate("/billing")}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Upgrade to Pro
        </button>
      </div>
    );
  }

  // ── Sections 2 + 3: create + table ───────────────────────────────────────
  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">API Keys</h1>
          <p className="text-sm text-gray-600">
            Programmatic access to QRFlow. Keys are shown once at creation.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Create New API Key
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Prefix</th>
              <th className="px-4 py-3 font-medium">Scopes</th>
              <th className="px-4 py-3 font-medium">Last Used</th>
              <th className="px-4 py-3 font-medium">Expires</th>
              <th className="px-4 py-3 font-medium">Status</th>
              {canManage && <th className="px-4 py-3 font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {keys.length === 0 ? (
              <tr>
                <td
                  colSpan={canManage ? 7 : 6}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No API keys yet.
                </td>
              </tr>
            ) : (
              keys.map((k) => (
                <tr key={k.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {k.name || "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                    {k.key_prefix}…
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes?.length ? (
                        k.scopes.map((s) => <ScopeBadge key={s} scope={s} />)
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {fmtDate(k.last_used_at)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {fmtDate(k.expires_at)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold ${
                        k.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {k.is_active ? "Active" : "Revoked"}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-3">
                      <button
                        onClick={() =>
                          setConfirmRevoke({ id: k.id, name: k.name || "this key" })
                        }
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Revoke
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateApiKeyModal
          onCreate={handleCreate}
          onClose={() => setShowCreate(false)}
          creating={creating}
        />
      )}

      {revealKey && (
        <KeyRevealModal rawKey={revealKey} onClose={() => setRevealKey(null)} />
      )}

      {confirmRevoke && (
        <ConfirmDialog
          title="Revoke API key"
          message={`Revoke key '${confirmRevoke.name}'? This cannot be undone.`}
          confirmLabel="Revoke"
          dangerous
          loading={revoking}
          onConfirm={handleRevoke}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}
    </div>
  );
}

export default ApiKeysPage;
